import * as React from "react";
import Paper from "@mui/material/Paper";
import { Box, Typography } from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { searchAPI } from "../API/Seach";
import Loader from "../Components/Loader";
import { useNavigate } from "react-router-dom";
import { tokens } from "../theme/tokens";
import {
  mapApiItemToRow,
  buildCompleteDetailsPayloadFromRow,
} from "../shared/bookingHistoryShared";
import { generateAndSaveBookingVoucherPdf } from "../Utils/bookingVoucherPdf";
import BookingHistoryDataTable from "../Components/BookingHistoryDataTable";

export default function TransactionHistory() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [downloadingBookingId, setDownloadingBookingId] = React.useState(null);
  const navigate = useNavigate();

  const loadBookings = React.useCallback(async () => {
    const rawId = localStorage.getItem("userId");
    const userId = parseInt(rawId, 10);
    if (!rawId || Number.isNaN(userId)) {
      toast.error("User not found. Please sign in again.");
      setRows([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await searchAPI.getBookingsByUser(
        userId,
        page + 1,
        rowsPerPage
      );
      const ok =
        response?.status === "success" &&
        Number(response?.statusCode) === 200 &&
        response.data;
      if (ok) {
        const items = response.data.items || [];
        setRows(items.map(mapApiItemToRow));
        setTotalCount(Number(response.data.totalCount) || items.length);
      } else {
        toast.error(response?.message || "Failed to fetch bookings");
        setRows([]);
        setTotalCount(0);
      }
    } catch (error) {
      toast.error("Failed to fetch bookings");
      console.error("Error fetching bookings:", error);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  React.useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const isPastBooking = (row) => {
    const dateVal = row?.checkOut || row?.checkIn;
    if (!dateVal) return false;
    const bookingDate = new Date(dateVal);
    if (Number.isNaN(bookingDate.getTime())) return false;
    bookingDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate < today;
  };

  const handleCancelBooking = async (row) => {
    const bookingId = row?.bookingId;
    if (bookingId == null || bookingId === "") {
      toast.error("Missing booking id.");
      return;
    }
    if (isPastBooking(row)) {
      toast.info("You cannot cancel past bookings.");
      return;
    }
    try {
      const response = await searchAPI.cancelHotelBookingById(bookingId);
      const ok =
        response?.status === "success" &&
        Number(response?.statusCode) === 200;
      if (ok) {
        toast.success("Booking cancelled successfully");
        await loadBookings();
      } else {
        toast.error(response?.message || "Failed to cancel booking");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to cancel booking";
      toast.error(msg);
      console.error("Error canceling booking:", error);
    }
  };

  const handleViewBooking = (row) => {
    const payload = buildCompleteDetailsPayloadFromRow(row);
    if (!payload) {
      toast.error("Missing booking reference. Please refresh the list.");
      return;
    }
    const q = new URLSearchParams({
      bookingId: String(payload.bookingId),
      supplierBookingId: payload.supplierBookingId,
      clientId: String(payload.clientId),
      userId: String(payload.userId),
    });
    navigate(`/booking-detail?${q.toString()}`);
  };

  const handleDownloadPdf = async (row) => {
    const payload = buildCompleteDetailsPayloadFromRow(row);
    if (!payload) {
      toast.error("Missing booking reference. Please refresh the list.");
      return;
    }
    setDownloadingBookingId(row.bookingId);
    try {
      const response = await searchAPI.getBookingCompleteDetails(payload);
      const ok =
        response?.status === "success" &&
        Number(response?.statusCode) === 200 &&
        response?.data;
      if (ok) {
        await generateAndSaveBookingVoucherPdf(response.data);
        toast.success("Booking voucher downloaded.");
      } else {
        toast.error(response?.message || "Could not load booking for PDF.");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to download PDF.";
      toast.error(msg);
      console.error("PDF download:", error);
    } finally {
      setDownloadingBookingId(null);
    }
  };

  if (loading && rows.length === 0 && totalCount === 0) {
    return <Loader />;
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box
        sx={{
          background: tokens.surface,
          minHeight: "100vh",
          width: "100%",
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: { xs: "88px", md: "96px" },
          pb: 4,
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: "100%",
            mx: 0,
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: `${tokens.radiusLg}px`,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowMd,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2.5,
              pb: 2,
              borderBottom: `1px solid ${tokens.border}`,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: `${tokens.radiusMd}px`,
                bgcolor: tokens.accentSoft,
                color: tokens.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HistoryOutlinedIcon />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: tokens.fontDisplay,
                  fontWeight: 700,
                  color: tokens.brand,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                Booking history
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Your hotel reservations and quick actions
              </Typography>
            </Box>
          </Box>

          <BookingHistoryDataTable
            rows={rows}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onView={handleViewBooking}
            onCancel={handleCancelBooking}
            onDownload={handleDownloadPdf}
            downloadingBookingId={downloadingBookingId}
          />
        </Paper>
      </Box>
    </>
  );
}
